import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { SPACE_CONTEXT_KEY } from '@modules/common/injection-tokens/space-context-key.injection-token';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { type Tab } from '@modules/common/types/tab';
import { type Upload } from '@modules/common/types/upload';
import { CommentsModule } from '@modules/comments/comments.module';
import { ConnectionsComponent } from '@modules/connections/components/connections/connections.component';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { ElementsModule } from '@modules/elements/elements.module';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { EditableTextareaComponent } from '@modules/form-controls/components/editable-textarea/editable-textarea.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { KnotsComponent } from '@modules/knots/components/knots/knots.component';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { StitchHubComponent } from '@modules/elements/components/stitch-hub/stitch-hub.component';
import { StateKey } from '@modules/settings/types/state-key';
import { TagsComponent } from '@modules/tags/components/tags/tags.component';
import {
  SLIDES_DESCRIPTION_MAX_LENGTH,
  SLIDES_SHORT_TEXT_MAX_LENGTH,
} from '@sharedlib/dtos/dto/slides-app/slides-limits';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { catchError, of, shareReplay, tap, type Observable } from 'rxjs';
import { BoardActionsService } from '../../../services/board-actions.service';
import { BOARD_LIST_FOLLOWS_SEARCH, BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { BoardsListContextMenuDirective } from '../../menus/boards-list-context-menu.directive';
import { BoardsDialogService } from '../../../services/boards-dialog.service';
import { type BoardFolder } from '../../../types/board';
import { toBoardFolderStitchSummary } from '../../shared/board-stitch-summary.util';
import { boardsFromDragData } from '../../shared/board-drag-data.util';
import { folderDescriptionFits, folderDescriptionToHtml, folderDescriptionToText } from '../../shared/folder-description.util';
import { BoardsFolderContextMenuComponent } from '../../menus/boards-folder-context-menu/boards-folder-context-menu.component';
import { BoardsListComponent } from '../boards-list/boards-list.component';

type FolderTab = 'boards' | 'details' | 'attachments' | 'stitch' | 'comments';

/** A folder's main-panel form, using the same header, tabs and item controls as Mail. */
@Component({
  selector: 'app-boards-folder-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './boards-folder-preview.component.html',
  styleUrl: './boards-folder-preview.component.less',
  host: { class: 'relative flex h-full min-h-0 flex-col bg-white' },
  providers: [BoardListStore, { provide: SPACE_CONTEXT_KEY, useValue: 'boards-folder-preview' }, { provide: BOARD_LIST_FOLLOWS_SEARCH, useValue: false }],
  imports: [Button2Component, IconComponent, InputComponent, PopoverDirective, ContextMenuWindowComponent,
    StitchTimestampsComponent, TabsComponent, CommentsModule, ElementsModule, FormControlsModule,
    ConnectionsComponent, KnotsComponent, TagsComponent, BoardsListComponent, BoardsFolderContextMenuComponent,
    LinkedInfoListComponent, EditableTextareaComponent, DragNDropModule, StitchHubComponent, EntityHubSearchComponent,
    ContextMenuStateIndicatorComponent, BoardsListContextMenuDirective],
})
export class BoardsFolderPreviewComponent {
  public readonly folder = input.required<BoardFolder>();
  public readonly closed = output<void>();
  public readonly dp = input(false);
  public readonly selectAllRequest = input(0);
  public readonly contentsSelected = output<void>();

  private readonly selectRequestedContents = effect(() => {
    const request = this.selectAllRequest();
    if (request) untracked(() => {
      this.selectContents();
      this.contentsSelected.emit();
    });
  });

  protected selectContents(): void {
    this.tab.set('boards');
    this.list.selectFolderContents(this.folder().id);
  }

  public prepareLeave(): Observable<boolean> {
    return this.formChanges.confirmUnsavedChanges(this.formKey);
  }
  public readonly boardOpened = output<string>();

  protected openBoard(id: string): void {
    if (!this.canLeave()) return;
    if (this.dp()) this.boardOpened.emit(id);
    else this.store.selectBoard(id);
  }
  protected readonly store = inject(BoardsStore);
  private readonly list = inject(BoardListStore);
  private readonly space = inject(SpaceContextStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions = inject(BoardActionsService);
  private readonly factory = inject(StitchServiceFactory);
  private readonly modal = inject(BoardsDialogService);
  private readonly injector = inject(Injector);
  private readonly formChanges = inject(FormChangesModalsService);
  private readonly permissionsService: PermissionsService<Actions, Subjects> = inject(PermissionsService);
  protected readonly permissionsLoaded = this.permissionsService.hasPermissionsLoaded;
  protected readonly hasKnowledgeHubAccess = computed(() =>
    this.permissionsService.checkPermission('read', ViewSubjects.knowledgeHubView),
  );
  private readonly formKey = `boards-folder-${crypto.randomUUID()}`;
  protected readonly StateKey = StateKey;
  protected readonly tab = signal<FolderTab>('boards');
  protected readonly displayKnowledge = signal(true);
  protected readonly saving = signal(false);
  protected readonly uploading = signal(false);
  protected readonly error = signal('');
  /** The details as last loaded or saved, the description as its text. Above the form, whose check reads it. */
  private readonly saved = signal({ title: '', description: '' });
  protected readonly form = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(SLIDES_SHORT_TEXT_MAX_LENGTH)] }),
    // A description left as it was is sent as stored, so only an edited one is held to the limit (Codex R02 MD-03).
    description: new FormControl('', { nonNullable: true, validators: [control => control.value === this.saved().description ? null : folderDescriptionFits(control)] }),
  });
  private readonly value = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
  protected readonly dirty = computed(() => this.value().title !== this.saved().title || this.value().description !== this.saved().description);
  protected readonly uploads = new FormControl<Upload[]>([], { nonNullable: true });
  protected readonly summary = computed<StitchItemSummaryDto>(() => toBoardFolderStitchSummary(this.folder(), this.space.contextId() ?? ''));
  protected readonly tabs: Tab<FolderTab>[] = [
    { title: 'Boards', value: 'boards' }, { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' }, { title: 'Stitch', value: 'stitch' }, { title: 'Comments', value: 'comments' },
  ];
  protected readonly canDropBoards = (data: DragData): boolean =>
    !this.folder().deleted && !this.folder().archived && this.movableBoardIds(data).length > 0;

  private movableBoardIds(data: DragData): string[] {
    return boardsFromDragData(data)
      .filter(board => board.folderId !== this.folder().id || board.archived || board.deleted)
      .map(board => board.id);
  }

  protected dropBoards(data: DragData): void {
    if (!this.canDropBoards(data)) return;
    this.store.moveBoardsToFolder(this.movableBoardIds(data), this.folder().id);
  }
  private readonly sync = effect(() => {
    const folder = this.folder();
    untracked(() => {
      if (!this.dirty() && !this.saving()) this.reset();
      const view = folder.deleted ? 'deleted' : folder.archived ? 'archived' : 'all';
      if (this.list.folderId() !== folder.id || this.list.view() !== view) this.list.setFolder(folder.id);
      if (folder.deleted) this.uploads.disable({ emitEvent: false });
      else this.uploads.enable({ emitEvent: false });
    });
  });

  constructor() {
    this.formChanges.registerForm(this.formKey, this.form, () => this.saveChanges(), () => this.reset(), { canLeave: () => this.canFinishNavigation() });
    this.destroyRef.onDestroy(() => this.formChanges.unregisterForm(this.formKey));
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value.title !== this.saved().title || value.description !== this.saved().description) this.form.markAsDirty();
      else this.form.markAsPristine();
    });
    this.destroyRef.onDestroy(this.store.registerEditorLeaveGuard(() => this.canLeave()));
    this.destroyRef.onDestroy(this.space.registerBeforeContextChange(() => of(this.canLeave())));
    const beforeUnload = (event: BeforeUnloadEvent): void => {
      if (this.dirty() || this.saving() || this.uploading() || this.uploads.value.length) event.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);
    this.destroyRef.onDestroy(() => window.removeEventListener('beforeunload', beforeUnload));
  }

  private canFinishNavigation(): boolean {
    if (this.saving() || this.uploading() || this.uploads.value.length) {
      this.error.set('Wait for the save or attachment upload to finish.');
      return false;
    }
    return true;
  }

  public canLeave(): boolean {
    if (!this.canFinishNavigation()) return false;
    if (!this.dirty()) return true;
    this.error.set('Save or cancel your folder changes before leaving.');
    return false;
  }

  protected reset(): void {
    if (this.saving()) return;
    // The description as it was typed: it is stored as HTML (SBUX-1142; Wook: "Fix both places").
    const value = { title: this.folder().title, description: folderDescriptionToText(this.folder().description) };
    this.saved.set(value);
    this.form.reset(value);
    this.error.set('');
  }

  protected save(): void {
    this.saveChanges().subscribe();
  }

  private saveChanges(): Observable<boolean> {
    if (this.saving() || this.folder().deleted) return of(false);
    const value = this.form.getRawValue();
    if (this.form.invalid || !value.title.trim()) {
      this.error.set('Enter a folder title of at most 255 characters and a description of at most ' + SLIDES_DESCRIPTION_MAX_LENGTH + ' characters.');
      return of(false);
    }
    this.saving.set(true);
    this.error.set('');
    // A description left as it was goes back as stored, its links and formatting included (Codex R02 MD-03); only one
    // edited here is the typed text, sent as HTML.
    const description = value.description === this.saved().description ? this.folder().description ?? '' : folderDescriptionToHtml(value.description);
    const result = this.store.saveFolderDetails(this.folder().id, value.title, description)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(catchError(() => of(false)))
      .pipe(tap(saved => {
        this.saving.set(false);
        if (saved) {
          const details = { title: value.title.trim(), description: value.description.trim() };
          this.saved.set(details);
          this.form.reset(details);
        } else this.error.set('Could not save the folder. Your changes are kept; try again.');
      }))
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    // The dialog may time out; the form still needs the eventual save result.
    result.subscribe();
    return result;
  }

  protected close(): void { if (this.canLeave()) this.closed.emit(); }

  protected archive(): void {
    if (!this.canLeave()) return;
    const folder = this.folder();
    if (folder.archived) this.store.updateFolderState(folder.id, { archived: false });
    else this.actions.changeFolderState(folder, 'archive');
  }

  protected trash(): void {
    if (!this.canLeave()) return;
    const folder = this.folder();
    if (folder.deleted) this.store.updateFolderState(folder.id, { deleted: false });
    else this.actions.changeFolderState(folder, 'trash');
  }

  protected openItem(item: StitchItemSummaryDto): void {
    if (!this.canLeave()) return;
    this.factory.getServiceByStitchType(item.itemType).getItem(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: fullItem => this.modal.openFullForm(fullItem, this.injector),
        error: () => this.error.set('Could not open this item. Try again.'),
      });
  }
}
